import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  UserGroupIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import {
  UserGroupIcon as UserGroupIconSolid,
  EnvelopeIcon as EnvelopeIconSolid
} from '@heroicons/react/24/solid';
import axiosInstance from '../api/axiosInstance';
import logger from '../../utils/logger';

const TeamModal = ({ open, onClose, onSubmit, team, type = 'team', mode = 'create' }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    email: '',
    type: type,
    privacy: 'private',
    settings: {
      allowMessaging: true,
      allowFileSharing: true,
      allowMemberInvites: false,
      autoAddNewUsers: false
    },
    deliverySettings: {
      deliveryMethod: 'alias',
      syncEnabled: true,
      deduplicationEnabled: true,
      createCanonicalEmail: true
    }
  });

  const [errors, setErrors] = useState({});
  const [activeSection, setActiveSection] = useState('basic');

  useEffect(() => {
    if (team && mode === 'edit') {
      setFormData({
        name: team.name || '',
        description: team.description || '',
        email: team.email || '',
        type: team.type || type,
        privacy: team.privacy || 'private',
        settings: {
          allowMessaging: team.settings?.allowMessaging ?? true,
          allowFileSharing: team.settings?.allowFileSharing ?? true,
          allowMemberInvites: team.settings?.allowMemberInvites ?? false,
          autoAddNewUsers: team.settings?.autoAddNewUsers ?? false
        },
        deliverySettings: {
          deliveryMethod: team.deliverySettings?.deliveryMethod || 'alias',
          syncEnabled: team.deliverySettings?.syncEnabled ?? true,
          deduplicationEnabled: team.deliverySettings?.deduplicationEnabled ?? true,
          createCanonicalEmail: team.deliverySettings?.createCanonicalEmail ?? true
        }
      });
    } else {
      // Reset form for new team
      setFormData({
        name: '',
        description: '',
        email: '',
        type: type,
        privacy: 'private',
        settings: {
          allowMessaging: true,
          allowFileSharing: true,
          allowMemberInvites: false,
          autoAddNewUsers: false
        },
        deliverySettings: {
          deliveryMethod: 'alias',
          syncEnabled: true,
          deduplicationEnabled: true,
          createCanonicalEmail: true
        }
      });
      setErrors({});
    }
  }, [team, mode, type, open]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (type === 'distribution_list' && !formData.email.trim()) {
      newErrors.email = 'Email is required for distribution lists';
    } else if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    // For distribution lists, validate email uniqueness
    if ((type === 'distribution_list' || formData.type === 'distribution_list') && formData.email) {
      try {
        // Check if email already exists as a user
        const userCheckResponse = await axiosInstance.get('/api/business/users', {
          params: { search: formData.email, limit: 1 }
        });

        if (userCheckResponse.data?.users?.some(u => u.email === formData.email)) {
          setErrors(prev => ({
            ...prev,
            email: 'This email is already in use by a user account. Please choose a different email alias.'
          }));
          return;
        }
      } catch (error) {
        logger.error('Error checking email uniqueness:', error);
      }
    }

    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      logger.error('Error submitting form:', error);
      // Check if error is about duplicate email
      if (error.response?.data?.message) {
        const errorMessage = error.response.data.message;
        if (errorMessage.includes('email') || errorMessage.includes('already exists')) {
          setErrors(prev => ({
            ...prev,
            email: errorMessage
          }));
        }
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="relative w-full max-w-2xl bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl transform transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                {type === 'distribution_list' ? (
                  <EnvelopeIconSolid className="w-5 h-5 text-white" />
                ) : (
                  <UserGroupIconSolid className="w-5 h-5 text-white" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {mode === 'edit' ? 'Edit' : 'Create New'} {type === 'team' ? 'Team' : 'Distribution List'}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {mode === 'edit' ? 'Update team information and settings' : 'Set up a new team for collaboration'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-[#222222] transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Section Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-800 px-6">
            <button
              onClick={() => setActiveSection('basic')}
              className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeSection === 'basic'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <InformationCircleIcon className="w-4 h-4" />
              <span>Basic Info</span>
            </button>
            <button
              onClick={() => setActiveSection('settings')}
              className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeSection === 'settings'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Cog6ToothIcon className="w-4 h-4" />
              <span>Settings</span>
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-6 max-h-[calc(100vh-300px)] overflow-y-auto">
            {activeSection === 'basic' && (
              <div className="space-y-5">
                {/* Name Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder={`Enter ${type === 'team' ? 'team' : 'distribution list'} name`}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors dark:bg-[#2a2a2a] dark:text-white ${
                      errors.name
                        ? 'border-red-500'
                        : 'border-gray-300 dark:border-gray-700'
                    }`}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                  )}
                </div>

                {/* Description Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Brief description of the team's purpose"
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors dark:bg-[#2a2a2a] dark:text-white resize-none"
                  />
                </div>

                {/* Email Field (for distribution lists) */}
                {type === 'distribution_list' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="team@yourdomain.com"
                      className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors dark:bg-[#2a2a2a] dark:text-white ${
                        errors.email
                          ? 'border-red-500'
                          : 'border-gray-300 dark:border-gray-700'
                      }`}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      This will be the primary email address for this distribution list
                    </p>
                  </div>
                )}

                {/* Privacy */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Privacy Level
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.privacy === 'private'
                        ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                        : 'border-gray-300 dark:border-gray-700 hover:border-gray-400'
                    }`}>
                      <input
                        type="radio"
                        name="privacy"
                        value="private"
                        checked={formData.privacy === 'private'}
                        onChange={(e) => handleInputChange('privacy', e.target.value)}
                        className="sr-only"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <ShieldCheckIcon className="w-5 h-5 text-orange-600" />
                          <span className="font-medium text-gray-900 dark:text-white">Private</span>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          Only invited members can access
                        </p>
                      </div>
                      {formData.privacy === 'private' && (
                        <CheckCircleIcon className="w-5 h-5 text-orange-600 flex-shrink-0" />
                      )}
                    </label>

                    <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.privacy === 'public'
                        ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                        : 'border-gray-300 dark:border-gray-700 hover:border-gray-400'
                    }`}>
                      <input
                        type="radio"
                        name="privacy"
                        value="public"
                        checked={formData.privacy === 'public'}
                        onChange={(e) => handleInputChange('privacy', e.target.value)}
                        className="sr-only"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <UserGroupIcon className="w-5 h-5 text-orange-600" />
                          <span className="font-medium text-gray-900 dark:text-white">Public</span>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          Anyone in organization can join
                        </p>
                      </div>
                      {formData.privacy === 'public' && (
                        <CheckCircleIcon className="w-5 h-5 text-orange-600 flex-shrink-0" />
                      )}
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'settings' && (
              <div className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                  <div className="flex items-start space-x-2">
                    <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-blue-900 dark:text-blue-300">Team Capabilities</h4>
                      <p className="text-sm text-blue-800 dark:text-blue-400 mt-1">
                        Configure what team members can do
                      </p>
                    </div>
                  </div>
                </div>

                {/* Toggle Settings */}
                {[
                  {
                    key: 'allowMessaging',
                    label: 'Allow Messaging',
                    description: 'Members can send messages in team channels'
                  },
                  {
                    key: 'allowFileSharing',
                    label: 'Allow File Sharing',
                    description: 'Members can upload and share files'
                  },
                  {
                    key: 'allowMemberInvites',
                    label: 'Allow Member Invites',
                    description: 'Members can invite others to join the team'
                  },
                  {
                    key: 'autoAddNewUsers',
                    label: 'Auto-add New Users',
                    description: 'Automatically add new organization members'
                  }
                ].map((setting) => (
                  <div key={setting.key} className="flex items-center justify-between py-4 border-b border-gray-200 dark:border-gray-800 last:border-0">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 dark:text-white">{setting.label}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{setting.description}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4">
                      <input
                        type="checkbox"
                        checked={formData.settings[setting.key]}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: {
                            ...formData.settings,
                            [setting.key]: e.target.checked
                          }
                        })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 rounded-full peer dark:bg-[#2a2a2a] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-700 peer-checked:bg-orange-600"></div>
                    </label>
                  </div>
                ))}
              </div>
            )}

          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#111111]">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#2a2a2a] border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#222222] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-sm hover:shadow-md"
            >
              <CheckCircleIcon className="w-4 h-4" />
              <span>{mode === 'edit' ? 'Save Changes' : 'Create'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamModal;
